import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUpdateDestinationDirectionTipsDialogValidationSchema } from './schema/update-destination-direction-tips-dialog-validation-schema';

import { InputField } from '@/components/atoms/input-field';
import { Dialog, DialogActions as DialogActionsInterface } from '@/components/molecules/dialog';
import { DestinationDirectionTips } from '@/features/destinations/api/types/destination';
import { useUpdateDestination } from '@/features/destinations/hooks/mutation/use-update-destination';
import { Trash } from '@/icons/trash';

interface Props extends DialogActionsInterface {
  destinationId: number;
  directionTips?: DestinationDirectionTips[];
}

interface FormValues {
  directionTips: {
    pl: string;
    en: string;
    ua: string;
  }[];
}

const mapDirectionTipsToRows = (directionTips?: DestinationDirectionTips[]) => {
  const tipsByLanguage = {
    pl: directionTips?.find(item => item.language === 'pl')?.tips.map(tip => tip.content) || [],
    en: directionTips?.find(item => item.language === 'en')?.tips.map(tip => tip.content) || [],
    ua: directionTips?.find(item => item.language === 'ua')?.tips.map(tip => tip.content) || [],
  };

  const rowCount = Math.max(
    tipsByLanguage.pl.length,
    tipsByLanguage.en.length,
    tipsByLanguage.ua.length,
    1,
  );

  return Array.from({ length: rowCount }, (_, index) => ({
    pl: tipsByLanguage.pl[index] || '',
    en: tipsByLanguage.en[index] || '',
    ua: tipsByLanguage.ua[index] || '',
  }));
};

export const UpdateDestinationDirectionTipsDialog = ({
  destinationId,
  directionTips,
  open,
  onCancel,
}: Props) => {
  const { t } = useTranslation(['common', 'destination']);
  const validationSchema = useUpdateDestinationDirectionTipsDialogValidationSchema();

  const defaultValues = useMemo<FormValues>(
    () => ({
      directionTips: mapDirectionTipsToRows(directionTips),
    }),
    [directionTips],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty, errors },
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'directionTips',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(defaultValues);
  }, [defaultValues, open, reset]);

  const { mutate, isPending } = useUpdateDestination(onCancel);

  const onSubmit = (data: FormValues) => {
    const formattedDirectionTips = (
      Object.entries({
        pl: data.directionTips.map(tip => tip.pl.trim()).filter(Boolean),
        en: data.directionTips.map(tip => tip.en.trim()).filter(Boolean),
        ua: data.directionTips.map(tip => tip.ua.trim()).filter(Boolean),
      }) as ['pl' | 'en' | 'ua', string[]][]
    )
      .filter(([, tips]) => tips.length > 0)
      .map(([language, tips]) => ({
        language,
        tips,
      }));

    mutate({
      destinationId,
      payload: {
        directionTips: formattedDirectionTips,
      },
    });
  };

  const directionTipsError =
    typeof errors.directionTips?.message === 'string' ? errors.directionTips.message : undefined;

  return (
    <Dialog
      title={t('destination:updateDirectionTipsDialogTitle')}
      onCancel={onCancel}
      open={open}
      fullWidth
      maxWidth="lg"
      confirmButtonText={t('common:button.edit')}
      isConfirmButtonDisabled={!isValid || !isDirty}
      isConfirmButtonSubmitting={isPending}
      onConfirm={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item md={0.8} xs={12}>
          <Typography variant="subtitle2">#</Typography>
        </Grid>
        <Grid item md={3.4} xs={12}>
          <Typography variant="subtitle2">{t('destination:translations.polish')}</Typography>
        </Grid>
        <Grid item md={3.4} xs={12}>
          <Typography variant="subtitle2">{t('destination:translations.english')}</Typography>
        </Grid>
        <Grid item md={3.4} xs={12}>
          <Typography variant="subtitle2">{t('destination:translations.ukrainian')}</Typography>
        </Grid>
        <Grid item md={1} xs={12} />

        {fields.map((field, index) => (
          <Grid container item spacing={2} key={field.id} alignItems="center">
            <Grid item md={0.8} xs={12}>
              <Typography color="text.secondary" variant="body2">
                {index + 1}
              </Typography>
            </Grid>
            <Grid item md={3.4} xs={12}>
              <Controller
                control={control}
                name={`directionTips.${index}.pl`}
                render={({ field, fieldState: { error } }) => (
                  <InputField
                    {...field}
                    fullWidth
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={3.4} xs={12}>
              <Controller
                control={control}
                name={`directionTips.${index}.en`}
                render={({ field, fieldState: { error } }) => (
                  <InputField
                    {...field}
                    fullWidth
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={3.4} xs={12}>
              <Controller
                control={control}
                name={`directionTips.${index}.ua`}
                render={({ field, fieldState: { error } }) => (
                  <InputField
                    {...field}
                    fullWidth
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={1} xs={12}>
              <IconButton
                color="error"
                type="button"
                aria-label={t('common:button.delete')}
                onClick={() => remove(index)}>
                <Trash />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        {directionTipsError && (
          <Grid item xs={12}>
            <Typography color="error.main" variant="caption">
              {directionTipsError}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ mt: 1 }}>
            <Button
              color="primary"
              variant="outlined"
              type="button"
              disabled={fields.length >= 50}
              onClick={() => append({ pl: '', en: '', ua: '' })}>
              {t('destination:directionTips.addRow')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};
